import React, { useState } from 'react';
import { toast } from 'sonner';
import type { DayKey, TimesheetEntry } from '@/types/timesheet';
import type { OvertimeClassificationResult } from '../OvertimeClassificationModal';

export interface UseTimesheetOvertimeClassificationParams {
  entries: TimesheetEntry[];
  setEntries: React.Dispatch<React.SetStateAction<TimesheetEntry[]>>;
  overtimeEnabled: boolean;
  isReadOnly: boolean;
}

export interface UseTimesheetOvertimeClassificationResult {
  classifyOvertimeEntry: TimesheetEntry | null;
  setClassifyOvertimeEntry: React.Dispatch<React.SetStateAction<TimesheetEntry | null>>;
  classifyOvertimeSuggestedHours: Partial<Record<DayKey, number>>;
  setClassifyOvertimeSuggestedHours: React.Dispatch<React.SetStateAction<Partial<Record<DayKey, number>>>>;
  handleAIClassifyOvertime: (suggestedOvertimeHours: Partial<Record<string, number>>) => void;
  handleClassifyOvertimeConfirm: (result: OvertimeClassificationResult) => void;
}

/** Drives the overtime classification modal, including the AI-suggested entry point. */
export function useTimesheetOvertimeClassification({
  entries,
  setEntries,
  overtimeEnabled,
  isReadOnly,
}: UseTimesheetOvertimeClassificationParams): UseTimesheetOvertimeClassificationResult {
  const [classifyOvertimeEntry, setClassifyOvertimeEntry] = useState<TimesheetEntry | null>(null);
  /** Per-day OT suggestions pre-populated by the AI "Review overtime hours" CTA (task 3.11) */
  const [classifyOvertimeSuggestedHours, setClassifyOvertimeSuggestedHours] = useState<Partial<Record<DayKey, number>>>({});

  /**
   * Task 3.11 — Called by AIInsightsSheet when the user clicks "Review overtime hours"
   * on a `contracted-hours-exceeded` warning. Finds the largest non-OT manual row
   * by total hours and opens the OvertimeClassificationModal pre-populated with
   * the per-day suggestions from the AI warning.
   */
  const handleAIClassifyOvertime = (suggestedOvertimeHours: Partial<Record<string, number>>) => {
    // Only act when overtime is enabled and timesheet is editable
    if (!overtimeEnabled || isReadOnly) return;

    // Find the best candidate row: largest total hours among non-OT manual (non-scheduled, non-absence) entries
    const candidateEntry = entries
      .filter((e) => !e.isFromSchedule && !e.absenceType && !e.overtimeTypeId)
      .sort((a, b) => {
        const totalA = Object.values(a.hours).reduce((s, h) => s + h, 0);
        const totalB = Object.values(b.hours).reduce((s, h) => s + h, 0);
        return totalB - totalA;
      })[0] ?? null;

    if (!candidateEntry) return;

    // Store suggested hours so the modal can pre-populate them
    setClassifyOvertimeSuggestedHours(suggestedOvertimeHours as Partial<Record<DayKey, number>>);
    setClassifyOvertimeEntry(candidateEntry);
  };

  /**
   * Called by OvertimeClassificationModal on confirm (task 2.17).
   * Replaces the original entry with the standard-hours version and inserts
   * the new OT row immediately after it.
   */
  const handleClassifyOvertimeConfirm = (result: OvertimeClassificationResult) => {
    setEntries((prev) => {
      const idx = prev.findIndex((e) => e.id === result.updatedEntry.id);
      if (idx === -1) {
        // Fallback: just append the new OT row
        return [...prev, result.newOvertimeEntry];
      }
      return [
        ...prev.slice(0, idx),
        result.updatedEntry,
        result.newOvertimeEntry,
        ...prev.slice(idx + 1),
      ];
    });
    setClassifyOvertimeEntry(null);
    toast.success('Overtime classified — new row added below');
  };

  return {
    classifyOvertimeEntry,
    setClassifyOvertimeEntry,
    classifyOvertimeSuggestedHours,
    setClassifyOvertimeSuggestedHours,
    handleAIClassifyOvertime,
    handleClassifyOvertimeConfirm,
  };
}
